import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, Mail, ShieldCheck, Zap, Sparkles, LogOut, CheckCircle2,
  Lock, ArrowRight, CreditCard, Clock, Save, Loader2, X
} from "lucide-react";
import PageHead from "@/components/PageHead";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PricingCards from "@/components/PricingCards";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function UserProfile() {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ name: name.trim() });
      toast.success("User profile updated successfully");
    } catch (_) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  return (
    <div>
      <PageHead
        label="User Profile & Account"
        title="Your Account & Wallet"
        sub="Manage your personal account credentials, security settings, and credit wallet."
      />

      <div className="grid gap-6 lg:grid-cols-3 items-start">
        {/* Left 2 Columns: Personal Details & Security */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Account Information */}
          <div className="card-surface p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-4 border-b border-border pb-6">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-electric via-violet to-aqua text-2xl font-black text-white shadow-float">
                {user?.name?.[0]?.toUpperCase() ?? "U"}
              </span>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground">{user?.name || "User"}</h2>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <Mail className="h-3.5 w-3.5 text-electric" /> {user?.email}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 text-[11px] font-bold text-emerald-500">
                    <ShieldCheck className="h-3 w-3" /> Active Account
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground capitalize">
                    Role: {user?.role ? user.role.replace("_", " ") : "Job Seeker"}
                  </span>
                </div>
              </div>
            </div>

            {/* Editable Profile Form */}
            <form onSubmit={handleSaveName} className="mt-6 space-y-4">
              <h3 className="text-sm font-bold text-foreground">Edit Account Details</h3>
              <div>
                <label className="label-caps block mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium outline-none focus:border-electric transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="label-caps block mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="w-full rounded-xl border border-border bg-secondary/60 px-4 py-2.5 text-sm text-muted-foreground cursor-not-allowed"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Email is your primary login credential and cannot be changed directly.
                </p>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={saving || name.trim() === user?.name}
                  className="cv-btn-primary text-xs py-2 px-5 flex items-center gap-1.5 disabled:opacity-50 shadow-xs"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>

          {/* Account Security & Actions */}
          <div className="card-surface p-6 shadow-sm">
            <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
              <Lock className="h-4 w-4 text-violet" /> Security & Session
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Your password is encrypted with modern scrypt and PBKDF2 cryptography.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
              <div>
                <p className="text-xs font-semibold text-foreground">Active Session</p>
                <p className="text-[11px] text-muted-foreground">Signed in as {user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-xl border border-coral/30 bg-coral/10 px-4 py-2 text-xs font-bold text-coral hover:bg-coral/20 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" /> Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Wallet & Credit Usage Card */}
        <div className="space-y-6">
          {/* Wallet & Credits Card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-surface p-6 border-t-4 border-t-electric shadow-float"
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <Zap className="h-4 w-4 text-electric fill-electric" /> Wallet Balance
              </span>
              <span className="rounded-full bg-electric/10 px-3 py-0.5 text-xs font-bold text-electric">
                {user?.plan || "Free"} Plan
              </span>
            </div>

            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-heading text-4xl font-extrabold text-foreground">{user?.credits ?? 0}</span>
              <span className="text-sm text-muted-foreground font-semibold">Credits available</span>
            </div>

            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Credits power AI ATS scoring, CV customization, STAR feedback, and interview simulations.
            </p>

            <button
              onClick={() => setPricingOpen(true)}
              className="cv-btn-primary mt-6 w-full justify-center text-xs py-3 shadow-glow"
              data-testid="profile-get-credits-btn"
            >
              <Sparkles className="h-4 w-4" /> Get More Credits / Upgrade
            </button>
          </motion.div>

          {/* Module Credit Costs Reference */}
          <div className="card-surface p-5 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Credit Usage Table
            </h4>
            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between border-b border-border/50 pb-2">
                <span className="text-muted-foreground">ATS Compatibility Check</span>
                <span className="font-bold text-electric">1 Credit</span>
              </div>
              <div className="flex items-center justify-between border-b border-border/50 pb-2">
                <span className="text-muted-foreground">CV Customization & Rewrite</span>
                <span className="font-bold text-electric">3 Credits</span>
              </div>
              <div className="flex items-center justify-between border-b border-border/50 pb-2">
                <span className="text-muted-foreground">STAR Answer Feedback</span>
                <span className="font-bold text-electric">1 Credit</span>
              </div>
              <div className="flex items-center justify-between pb-1">
                <span className="text-muted-foreground">Face-to-Face 15-min Room</span>
                <span className="font-bold text-electric">10 Credits</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Purchase & Upgrade Modal Dialog Window */}
      <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-6 sm:p-8 bg-card border border-border">
          <DialogHeader className="mb-6 text-center sm:text-center">
            <span className="label-caps text-electric mx-auto mb-1">Account Top-Up</span>
            <DialogTitle className="font-heading text-2xl sm:text-3xl font-black">
              Upgrade Plan or Top Up Credits
            </DialogTitle>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-lg mx-auto mt-1">
              Select a monthly subscription or purchase instant top-up credit packs with 90-day rollover validity.
            </p>
          </DialogHeader>

          <PricingCards />
        </DialogContent>
      </Dialog>
    </div>
  );
}
